import { Injectable, computed, signal } from '@angular/core';
import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { signOut } from 'firebase/auth';
import { UiAuthService } from '@smatchy/ui-auth';
import { environment } from '../environments/environment';
import { getFirebaseAuth, waitForFirebaseAuthState } from './firebase-desktop';

export interface AuthSessionUserSummary {
  id: string;
  roles: string[];
}

export interface AuthSessionResponse {
  user: AuthSessionUserSummary;
}

export interface RefreshResponse {
  access_token: string;
  user: AuthSessionUserSummary;
}

@Injectable({
  providedIn: 'root',
})
export class BackofficeSessionService {
  /** The current admin's effective roles (superadmin included, from the allowlist). */
  readonly roles = signal<string[]>([]);
  readonly isSuperadmin = computed(() => this.roles().includes('superadmin'));

  private readonly rawHttpClient: HttpClient;
  private bootstrapInFlight?: Promise<void>;
  private refreshInFlight?: Promise<RefreshResponse>;
  private sessionGeneration = 0;

  constructor(
    private readonly authService: UiAuthService,
    private readonly httpBackend: HttpBackend,
    private readonly router: Router,
  ) {
    this.rawHttpClient = new HttpClient(this.httpBackend);
  }

  initialize(): Promise<void> {
    if (this.bootstrapInFlight) {
      return this.bootstrapInFlight;
    }

    const bootstrapPromise = (async () => {
      try {
        await this.bootstrapSession();
      } finally {
        this.bootstrapInFlight = undefined;
      }
    })();
    this.bootstrapInFlight = bootstrapPromise;
    return bootstrapPromise;
  }

  async waitForPendingSessionWork(): Promise<void> {
    if (this.bootstrapInFlight) {
      await this.bootstrapInFlight;
    }
    if (this.refreshInFlight) {
      await this.refreshInFlight;
    }
  }

  hasPendingSessionWork(): boolean {
    return Boolean(this.bootstrapInFlight || this.refreshInFlight);
  }

  async refreshSession(): Promise<RefreshResponse> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    const generation = this.sessionGeneration;
    const refreshPromise = (async () => {
      try {
        const response = await this.createRefreshSession();
        if (generation === this.sessionGeneration) {
          this.authService.applyRefreshedSession(response.access_token, response.user);
          this.roles.set(response.user.roles ?? []);
        }
        return response;
      } finally {
        this.refreshInFlight = undefined;
      }
    })();
    this.refreshInFlight = refreshPromise;
    return refreshPromise;
  }

  endSession(options: { navigate: boolean } = { navigate: true }): void {
    this.sessionGeneration += 1;
    this.roles.set([]);
    this.authService.reset();
    void signOut(getFirebaseAuth()).catch(() => undefined);
    if (options.navigate) {
      void this.router.navigate([this.authService.getLoginRoute()]);
    }
  }

  isAuthTokenInvalid(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status === 401 && error.error?.code === 'AUTH_TOKEN_INVALID';
  }

  isHardSessionFailure(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status === 401;
  }

  private async bootstrapSession(): Promise<void> {
    await waitForFirebaseAuthState();
    const token = this.authService.getAccessToken();
    if (!token) {
      return;
    }

    try {
      const session = await this.validateCurrentSession(token);
      if (!this.isAdmin(session.user)) {
        this.authService.reset();
        return;
      }
      this.authService.applyRefreshedSession(token, session.user);
      this.roles.set(session.user.roles ?? []);
    } catch (error: unknown) {
      if (!this.isAuthTokenInvalid(error)) {
        return;
      }

      if (!getFirebaseAuth().currentUser) {
        this.endSession({ navigate: false });
        return;
      }

      try {
        const response = await this.refreshSession();
        if (!this.isAdmin(response.user)) {
          this.endSession({ navigate: false });
        }
      } catch (refreshError: unknown) {
        if (this.isHardSessionFailure(refreshError)) {
          this.endSession({ navigate: false });
        }
      }
    }
  }

  private validateCurrentSession(token: string): Promise<AuthSessionResponse> {
    return firstValueFrom(
      this.rawHttpClient.get<AuthSessionResponse>(`${environment.api}/auth/session`, {
        headers: new HttpHeaders({
          Authorization: `Bearer ${token}`,
        }),
      }),
    );
  }

  private async createRefreshSession(): Promise<RefreshResponse> {
    await waitForFirebaseAuthState();
    const firebaseUser = getFirebaseAuth().currentUser;
    if (!firebaseUser) {
      throw new HttpErrorResponse({ status: 401, statusText: 'Firebase session missing' });
    }
    const firebaseIdToken = await firebaseUser.getIdToken(true);
    return firstValueFrom(
      this.rawHttpClient.post<RefreshResponse>(`${environment.api}/auth/refresh`, { firebaseIdToken }),
    );
  }

  private isAdmin(user: AuthSessionUserSummary): boolean {
    return user.roles?.includes('admin') ?? false;
  }
}
